import { FundingPolicyType } from './appModel';
import { fundingPolicyTypeDefs } from './FundingPolicyTypeDef';

/**
 * All policy categories available for selection in the guided planning flow.
 * These are the 4 high-level categories shown on the options step.
 */
export const ALL_POLICY_CATEGORIES: GuidedPlanningPolicyCategory[] = [
  'traditional',
  'hybrid',
  'lifeWithRider',
  'annuityHybrid',
];

/**
 * Maps each category to its underlying policy types.
 * When a category is selected, all its policy types are used for filtering.
 */
export const POLICY_CATEGORY_MEMBERS: Record<
  GuidedPlanningPolicyCategory,
  GuidedPlanningPolicyType[]
> = {
  traditional: [
    FundingPolicyType.longTermCareInsurance,
    FundingPolicyType.jointLongTermCareInsurance,
  ],
  hybrid: [FundingPolicyType.hybridLifeInsurance, FundingPolicyType.jointHybridAssetBased],
  lifeWithRider: [FundingPolicyType.lifeInsuranceWithRider],
  annuityHybrid: [FundingPolicyType.annuityHybrid],
};

/**
 * Human-readable labels for policy categories.
 */
export const POLICY_CATEGORY_LABELS: Record<GuidedPlanningPolicyCategory, string> = {
  traditional: 'Traditional LTC Insurance',
  hybrid: 'Hybrid Life Insurance',
  lifeWithRider: 'Life Insurance with LTC Rider',
  annuityHybrid: 'Annuity with LTC Benefits',
};

/**
 * Descriptions for policy categories.
 */
export const POLICY_CATEGORY_DESCRIPTIONS: Record<GuidedPlanningPolicyCategory, string> = {
  traditional:
    "Standalone LTC coverage with flexible benefits. Premiums may increase; no payout if care isn't needed.",
  hybrid:
    "Life insurance with built-in LTC benefits. Guaranteed premiums with value returned if care isn't needed.",
  lifeWithRider:
    'Life insurance with an LTC rider that accelerates the death benefit for care expenses.',
  annuityHybrid:
    'Converts existing assets into leveraged LTC benefits (typically 2-3x premium). Principal preserved if unused.',
};

/**
 * Expands selected categories to their underlying policy types for quote filtering.
 */
export function expandCategoriesToPolicyTypes(
  categories: GuidedPlanningPolicyCategory[],
): GuidedPlanningPolicyType[] {
  const result: GuidedPlanningPolicyType[] = [];
  for (const category of categories) {
    result.push(...POLICY_CATEGORY_MEMBERS[category]);
  }
  return result;
}

/**
 * All individual policy types (for backward compatibility and quote filtering).
 * @deprecated Use ALL_POLICY_CATEGORIES for options step UI.
 */
export const GUIDED_PLANNING_POLICY_TYPES: GuidedPlanningPolicyType[] = [
  FundingPolicyType.longTermCareInsurance,
  FundingPolicyType.jointLongTermCareInsurance,
  FundingPolicyType.lifeInsuranceWithRider,
  FundingPolicyType.hybridLifeInsurance,
  FundingPolicyType.jointHybridAssetBased,
  FundingPolicyType.annuityHybrid,
];

/**
 * Category mappings for death benefits preference.
 */
const DEATH_BENEFITS_CATEGORY_MAPPING: Record<'yes' | 'no', GuidedPlanningPolicyCategory[]> = {
  yes: ['lifeWithRider', 'hybrid'],
  no: ['traditional', 'annuityHybrid'],
};

/**
 * Category mappings for payment preference.
 */
const PAYMENT_PREFERENCE_CATEGORY_MAPPING: Record<
  'upfront' | 'monthly' | 'balanced',
  GuidedPlanningPolicyCategory[]
> = {
  upfront: ['hybrid', 'annuityHybrid', 'lifeWithRider'],
  monthly: ['traditional'],
  balanced: ['traditional', 'hybrid', 'lifeWithRider', 'annuityHybrid'],
};

type PaymentPreferenceWithFilter = Exclude<PaymentPreference, 'no-preference'>;

/**
 * Category mappings for benefit structure preference.
 */
const BENEFIT_STRUCTURE_CATEGORY_MAPPING: Record<
  'guaranteed' | 'max-value',
  GuidedPlanningPolicyCategory[]
> = {
  'guaranteed': ['hybrid', 'annuityHybrid', 'lifeWithRider'],
  'max-value': ['traditional'],
};

/**
 * Preferences collected during the guided planning flow.
 * These influence predicted policy types but do NOT directly filter quotes.
 */
export interface GuidedPlanningPreferences {
  deathBenefits?: 'yes' | 'no';
  paymentPreference?: 'upfront' | 'monthly' | 'balanced' | 'no-preference';
  benefitStructure?: 'max-value' | 'guaranteed';
}

/**
 * Payment option labels used for quote display and client-side filtering.
 * These are the normalized/grouped values shown to users.
 */
export type GuidedPlanningPaymentOption = 'Single Pay' | '10-Pay' | 'Monthly' | 'Annually';

/**
 * All available payment options for UI display
 */
export const ALL_PAYMENT_OPTIONS: GuidedPlanningPaymentOption[] = [
  'Single Pay',
  '10-Pay',
  'Monthly',
  'Annually',
];

/**
 * Raw payment option values that appear in quote data.
 * These get normalized to GuidedPlanningPaymentOption for display.
 */
export type RawPaymentOption =
  | '10-Pay'
  | '10 Years'
  | 'Annually'
  | 'Monthly'
  | 'Recurring Premium'
  | 'Single Pay'
  | 'Single Premium'
  | 'Single Premium Cash';

/**
 * Mapping from raw payment options to normalized display groups.
 * Multiple raw values can map to the same display group.
 */
export const PAYMENT_OPTION_GROUPS: Record<RawPaymentOption, GuidedPlanningPaymentOption> = {
  'Single Pay': 'Single Pay',
  'Single Premium': 'Single Pay',
  'Single Premium Cash': 'Single Pay',
  '10-Pay': '10-Pay',
  '10 Years': '10-Pay',
  'Monthly': 'Monthly',
  'Recurring Premium': '10-Pay',
  'Annually': 'Annually',
};

/**
 * Human-readable labels for payment option groups in the UI.
 */
export const PAYMENT_OPTION_LABELS: Record<GuidedPlanningPaymentOption, string> = {
  'Single Pay': 'Single Pay',
  'Monthly': 'Monthly',
  '10-Pay': '10-Pay',
  'Annually': 'Annual',
};

/** Maximum number of policy categories to recommend */
const MAX_RECOMMENDED_CATEGORIES = 2;

/**
 * Determines recommended policy categories based on user preferences.
 * Scores categories, picks top 2.
 *
 * If no preferences are set, returns empty array (user must select manually).
 */
export function getRecommendedPolicyCategoriesFromPreferences(
  preferences: GuidedPlanningPreferences | undefined,
): GuidedPlanningPolicyCategory[] {
  if (!preferences) {
    return [];
  }

  // Initialize scores for categories
  const categoryScores: Record<GuidedPlanningPolicyCategory, number> = {
    traditional: 0,
    hybrid: 0,
    lifeWithRider: 0,
    annuityHybrid: 0,
  };

  let hasAnyPreference = false;

  // Helper to score categories
  const scoreCategories = (categories: GuidedPlanningPolicyCategory[]) => {
    for (const category of categories) {
      categoryScores[category]++;
    }
  };

  // Score categories matching death benefits preference
  if (preferences.deathBenefits) {
    hasAnyPreference = true;
    scoreCategories(DEATH_BENEFITS_CATEGORY_MAPPING[preferences.deathBenefits]);
  }

  // Score categories matching payment preference
  if (preferences.paymentPreference && preferences.paymentPreference !== 'no-preference') {
    hasAnyPreference = true;
    scoreCategories(PAYMENT_PREFERENCE_CATEGORY_MAPPING[preferences.paymentPreference]);
  }

  // Score categories matching benefit structure preference
  if (preferences.benefitStructure) {
    hasAnyPreference = true;
    scoreCategories(BENEFIT_STRUCTURE_CATEGORY_MAPPING[preferences.benefitStructure]);
  }

  // If no meaningful preferences set, return empty
  if (!hasAnyPreference) {
    return [];
  }

  // Sort categories by score (highest first), filter to those with score > 0, take top 2
  return ALL_POLICY_CATEGORIES.filter(cat => categoryScores[cat] > 0)
    .sort((a, b) => categoryScores[b] - categoryScores[a])
    .slice(0, MAX_RECOMMENDED_CATEGORIES);
}

/**
 * User-facing feedback messages for each preference value.
 * These explain how preferences influence policy type predictions.
 */
export const PREFERENCE_FEEDBACK_MESSAGES = {
  deathBenefits: {
    yes: "We'll focus on hybrid and life insurance policies that include a death benefit for your loved ones.",
    no: "We'll focus on traditional LTC and annuity-based policies that maximize your care coverage.",
  },
  paymentPreference: {
    'upfront': "We'll focus on policies that offer single-pay options.",
    'monthly': "We'll focus on policies with ongoing monthly premium options.",
    'balanced': "We'll focus on policies with annual and 10-pay options.",
    'no-preference': "We'll show all payment options so you can compare.",
  },
  benefitStructure: {
    'max-value': "We'll focus on policies that maximize your LTC benefits.",
    'guaranteed':
      "We'll focus on policies that provide guaranteed value if care isn't needed, such as death benefits or return of premium.",
  },
  premiumBudget: {
    upfront:
      "We'll use this lump-sum amount to model coverage and benefits on the policy options we show you next.",
    monthly:
      "We'll use this ongoing monthly premium level to model coverage and benefits on the policy options we show you next.",
    balanced:
      "We'll use this premium budget to model annual and multi-pay contributions on the policy options we show you next.",
    'no-preference':
      "We'll use this amount as your target contribution when we price and compare policy options across payment styles.",
  },
} as const;

/**
 * Normalizes a raw payment option string to a grouped payment option.
 * Uses exact match first, then falls back to pattern matching.
 */
function normalizePaymentOption(rawPaymentOption: string): GuidedPlanningPaymentOption {
  if (rawPaymentOption in PAYMENT_OPTION_GROUPS) {
    return PAYMENT_OPTION_GROUPS[rawPaymentOption as RawPaymentOption];
  }

  const normalized = rawPaymentOption.toLowerCase();
  if (normalized.includes('single') || normalized.includes('premium cash')) return 'Single Pay';
  if (normalized.includes('10') || normalized.includes('ten')) return '10-Pay';
  if (normalized.includes('monthly') || normalized.includes('recurring')) return 'Monthly';
  if (normalized.includes('annual')) return 'Annually';

  return 'Single Pay';
}

/**
 * Extracts the payment option from a quote's payment fields.
 * Works with both PolicyQuote and TraditionalLTCQuote types.
 */
function getPaymentOptionFromQuoteFields(
  cobPaymentOption?: string | null,
  productFundingOption?: string | null,
  premiumFrequency?: string | null,
): GuidedPlanningPaymentOption {
  const paymentField = cobPaymentOption || productFundingOption || premiumFrequency || '';
  return normalizePaymentOption(paymentField);
}

/**
 * Quote fields needed for payment option extraction.
 */
export interface QuoteForPaymentOption {
  cobPaymentOption?: string | null;
  productFundingOption?: string | null;
  premiumFrequency?: string | null;
}

/**
 * Gets the human-readable label for a policy type from fundingPolicyTypeDefs.
 */
export function getPolicyTypeLabel(policyType: GuidedPlanningPolicyType): string {
  return fundingPolicyTypeDefs[policyType]?.label ?? 'Unknown Policy Type';
}

/**
 * Extracts the payment option from a quote. Exported for use in UI display.
 */
export function getQuotePaymentOption(quote: QuoteForPaymentOption): GuidedPlanningPaymentOption {
  return getPaymentOptionFromQuoteFields(
    quote.cobPaymentOption,
    quote.productFundingOption,
    quote.premiumFrequency,
  );
}

/**
 * Allowed normalized payment options per preference value.
 * This mapping is used to keep quote filtering behavior centralized across client and server.
 */
export const PAYMENT_PREFERENCE_ALLOWED_OPTIONS: Record<
  PaymentPreferenceWithFilter,
  GuidedPlanningPaymentOption[]
> = {
  upfront: ['Single Pay'],
  monthly: ['Monthly'],
  balanced: ['10-Pay', 'Annually'],
};

/**
 * Filters quotes by the selected payment preference.
 */
export function filterQuotesByPaymentPreference<T extends QuoteForPaymentOption>(
  quotes: T[],
  paymentPreference: PaymentPreference | undefined,
): T[] {
  if (!paymentPreference || paymentPreference === 'no-preference') {
    return quotes;
  }

  const allowedPaymentOptions = PAYMENT_PREFERENCE_ALLOWED_OPTIONS[paymentPreference];
  return quotes.filter(quote => allowedPaymentOptions.includes(getQuotePaymentOption(quote)));
}
